import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { IncompleteEnterpriseRow } from '@core/models/enterprise.model';
import { UserProfile } from '@core/models/user.model';
import { ApiMessageService } from '@core/services/api-message.service';
import { EnterpriseService } from '@features/enterprises/services/enterprise.service';
import { EnterpriseOnboardingService } from '@features/enterprises/services/enterprise-onboarding.service';
import { PageShell } from '@shared/components/page-shell/page-shell';
import { DataTable, DataTableAction, DataTableColumn } from '@shared/components/data-table/data-table';
import { extractContent, extractTotalElements } from '@shared/utils/response.utils';
import { buildEnterpriseRouteSegments } from '@shared/utils/enterprise.utils';
import {
  IncompleteResumeTarget,
  resolveIncompleteEnterpriseResume,
} from '@features/enterprises/utils/enterprise-onboarding-resume.utils';
import { OrganizationDetailsResponse } from '@core/models/org.model';
import { EnterpriseOnboardingProgress } from '@features/enterprises/constants/enterprise-onboarding.constants';
import {
  buildIncompleteEnterpriseListParams,
  DEFAULT_INCOMPLETE_ENTERPRISE_SORT,
  INCOMPLETE_ENTERPRISE_SORT_OPTIONS,
} from '@shared/utils/list-query.utils';

@Component({
  selector: 'app-incomplete-enterprise-list',
  imports: [PageShell, DataTable, FormsModule],
  templateUrl: './incomplete-enterprise-list.html',
  styleUrl: './incomplete-enterprise-list.scss',
})
export class IncompleteEnterpriseList implements OnInit {
  private readonly enterpriseService = inject(EnterpriseService);
  private readonly onboardingService = inject(EnterpriseOnboardingService);
  private readonly apiMessage = inject(ApiMessageService);
  private readonly router = inject(Router);

  private readonly profileByUid = new Map<string, UserProfile>();

  protected isInitialLoading = false;
  protected isRefreshing = false;
  protected page = 1;
  protected readonly size = 10;
  protected sortOption: 'asc' | 'desc' = DEFAULT_INCOMPLETE_ENTERPRISE_SORT;
  protected readonly sortOptions = INCOMPLETE_ENTERPRISE_SORT_OPTIONS;
  protected searchQuery = '';
  protected totalItems = 0;
  protected enterprises: IncompleteEnterpriseRow[] = [];
  protected hasLoadedOnce = false;

  protected readonly columns: DataTableColumn[] = [
    { key: 'role', label: 'Role', width: '9%' },
    { key: 'firstName', label: 'First Name', width: '11%' },
    { key: 'lastName', label: 'Last Name', width: '11%' },
    { key: 'email', label: 'Email', width: '15%' },
    { key: 'mobileNumber', label: 'Mobile', width: '11%' },
    { key: 'verifiedLabel', label: 'Verified', width: '8%' },
    { key: 'createdOnLabel', label: 'Created On', width: '11%' },
    { key: 'profileStatus', label: 'Status', width: '9%', type: 'status' },
    { key: 'workFlowStatus', label: 'Workflow', width: '11%' },
  ];

  protected readonly tableActions: DataTableAction[] = [
    { label: 'Continue', mode: 'complete' },
  ];

  protected isContinuing = false;

  ngOnInit(): void {
    this.loadEnterprises();
  }

  protected loadEnterprises(options?: { silent?: boolean }): void {
    const silent = options?.silent ?? this.hasLoadedOnce;

    if (silent) {
      this.isRefreshing = true;
    } else {
      this.isInitialLoading = true;
    }

    this.enterpriseService.getIncompleteEnterprises(this.buildListParams()).subscribe({
      next: (response) => {
        if (!response.success) {
          this.apiMessage.error(response, 'LOAD_INCOMPLETE_ENTERPRISES');
          if (!silent) {
            this.enterprises = [];
            this.profileByUid.clear();
          }
          this.isInitialLoading = false;
          this.isRefreshing = false;
          return;
        }

        this.profileByUid.clear();
        this.enterprises = extractContent<UserProfile>(response).map((profile) => this.mapEnterpriseRow(profile));
        this.totalItems = extractTotalElements(response);
        this.hasLoadedOnce = true;
        this.isInitialLoading = false;
        this.isRefreshing = false;
      },
      error: () => {
        this.apiMessage.error(null, 'LOAD_INCOMPLETE_ENTERPRISES');
        if (!silent) {
          this.enterprises = [];
          this.profileByUid.clear();
        }
        this.isInitialLoading = false;
        this.isRefreshing = false;
      },
    });
  }

  protected onFilterChange(): void {
    this.page = 1;
    this.loadEnterprises({ silent: this.hasLoadedOnce });
  }

  protected onPageChange(page: number): void {
    this.page = page;
    this.loadEnterprises({ silent: true });
  }

  protected onActionClicked(event: { mode: string; row: IncompleteEnterpriseRow }): void {
    if (event.mode !== 'complete' || this.isContinuing) {
      return;
    }

    const profile = this.profileByUid.get(event.row.uid);
    if (!profile) {
      this.apiMessage.error(null, 'LOAD_INCOMPLETE_ENTERPRISES');
      return;
    }

    const target = resolveIncompleteEnterpriseResume(profile);

    if (target.requiresOrganizationFetch && profile.organizationId) {
      this.isContinuing = true;
      this.enterpriseService.getOrganizationById(profile.organizationId).subscribe({
        next: (response) => {
          this.isContinuing = false;
          const orgDetails = response.success ? response.data : null;
          const resolvedTarget = this.resolveTargetWithOrganization(target, orgDetails);
          this.navigateToResumeStep(profile, resolvedTarget, orgDetails);
        },
        error: () => {
          this.isContinuing = false;
          this.navigateToResumeStep(profile, target, null);
        },
      });
      return;
    }

    this.navigateToResumeStep(profile, target, null);
  }

  private resolveTargetWithOrganization(
    target: IncompleteResumeTarget,
    orgDetails: OrganizationDetailsResponse | null,
  ): IncompleteResumeTarget {
    if (
      target.routeStep === 'payment-capture' &&
      !orgDetails?.subscriptionResponse
    ) {
      return {
        routeStep: 'add-plan',
        progress: EnterpriseOnboardingProgress.OrganizationCreated,
        requiresOrganizationFetch: false,
      };
    }

    return target;
  }

  private navigateToResumeStep(
    profile: UserProfile,
    target: IncompleteResumeTarget,
    orgDetails: OrganizationDetailsResponse | null,
  ): void {
    const routeStep = this.onboardingService.resumeIncompleteEnterprise(profile, target, orgDetails);

    void this.router.navigate(buildEnterpriseRouteSegments(routeStep), {
      state: this.onboardingService.buildNavigationState(),
    });
  }

  private buildListParams(): Record<string, string | number> {
    return buildIncompleteEnterpriseListParams({
      page: this.page - 1,
      size: this.size,
      sort: this.sortOption,
      search: this.searchQuery,
    });
  }

  private mapEnterpriseRow(profile: UserProfile): IncompleteEnterpriseRow {
    const uid = profile.profileIdentifiers?.[0]?.uid || '';
    if (uid) {
      this.profileByUid.set(uid, profile);
    }

    return {
      uid,
      role: profile.role || '-',
      firstName: profile.firstName || '-',
      lastName: profile.lastName || '-',
      email: profile.profileIdentifiers?.[0]?.emailId || '-',
      mobileNumber: profile.mobileNumber || '-',
      verifiedLabel: profile.mobileVerified ? 'Yes' : 'No',
      createdOnLabel: this.formatDate(profile.auditInfo?.createdOn),
      profileStatus: profile.profileStatus || '-',
      organizationName: profile.organizationName || '-',
      workFlowStatus: profile.workFlowStatus || '-',
    };
  }

  private formatDate(value?: string): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }
}
